export type OfflineSyncStatus = 'pendente' | 'sincronizando' | 'sincronizado' | 'erro'

export type OfflineActivityType =
  | 'operacao'
  | 'checklist'
  | 'auditoria'
  | 'relatorio'
  | 'evidencia_operacional'
  | 'evento_operacao'
  | 'historico_operacional'
  | 'ordem_servico'
  | 'tarefa_operacional'
  | 'operacao_update'
  | 'checklist_update'
  | 'auditoria_update'
  | 'relatorio_update'
  | 'ordem_servico_update'
  | 'tarefa_operacional_update'

export interface OfflineAttachment {
  id: string
  activityId: string
  fieldName: string
  bucket: string
  path: string
  fileName: string
  mimeType: string
  blob: Blob
  createdAt: number
}

export interface OfflineActivity {
  id: string
  clientRequestId: string
  type: OfflineActivityType
  payload: Record<string, unknown>
  attachments?: OfflineAttachmentMeta[]
  createdAt: number
  updatedAt: number
  syncStatus: OfflineSyncStatus
  attempts: number
  lastError?: string
}

export interface OfflineAttachmentMeta {
  id: string
  fieldName: string
  bucket: string
  path: string
  fileName: string
  mimeType: string
}

const DB_NAME = 'agroschelive-offline-db'
const DB_VERSION = 2
const ACTIVITIES_STORE = 'activities'
const ATTACHMENTS_STORE = 'attachments'
const MAX_AGE = 86400000

let dbPromise: Promise<IDBDatabase> | null = null

function openOfflineDb() {
  if (dbPromise) return dbPromise

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result

      if (!db.objectStoreNames.contains(ACTIVITIES_STORE)) {
        const store = db.createObjectStore(ACTIVITIES_STORE, { keyPath: 'id' })
        store.createIndex('createdAt', 'createdAt')
      }

      const activities = request.transaction?.objectStore(ACTIVITIES_STORE)
      if (activities && !activities.indexNames.contains('syncStatus')) {
        activities.createIndex('syncStatus', 'syncStatus')
      }
      if (activities && !activities.indexNames.contains('clientRequestId')) {
        activities.createIndex('clientRequestId', 'clientRequestId', { unique: true })
      }

      if (!db.objectStoreNames.contains(ATTACHMENTS_STORE)) {
        const store = db.createObjectStore(ATTACHMENTS_STORE, { keyPath: 'id' })
        store.createIndex('activityId', 'activityId')
        store.createIndex('createdAt', 'createdAt')
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => { dbPromise = null; reject(request.error) }
  })

  return dbPromise
}

function runStore<T>(storeName: string, mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T> | void) {
  return openOfflineDb().then((db) => new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(storeName, mode)
    const store = transaction.objectStore(storeName)
    const request = action(store)
    transaction.oncomplete = () => resolve(request ? request.result : (undefined as T))
    transaction.onerror = () => reject(transaction.error)
    transaction.onabort = () => reject(transaction.error)
  }))
}

function getAllFromStore<T>(storeName: string) {
  return runStore<T[]>(storeName, 'readonly', (store) => store.getAll())
}

function deleteFromStore(storeName: string, id: string) {
  return runStore(storeName, 'readwrite', (store) => store.delete(id))
}

export function createClientRequestId(prefix = 'request') {
  if ('crypto' in window && 'randomUUID' in crypto) return `${prefix}-${crypto.randomUUID()}`
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function isFresh(item: { createdAt: number }) { return Date.now() - item.createdAt <= MAX_AGE }

function normalizeActivity(item: OfflineActivity): OfflineActivity {
  return {
    ...item,
    clientRequestId: item.clientRequestId || item.id,
    syncStatus: item.syncStatus || 'pendente',
  }
}

export async function getOfflineQueue(): Promise<OfflineActivity[]> {
  const normalized = (await getAllFromStore<OfflineActivity>(ACTIVITIES_STORE) || []).map(normalizeActivity)
  const fresh = normalized.filter(isFresh)
  if (fresh.length !== normalized.length) await cleanupExpiredOfflineData()
  return fresh.sort((a, b) => a.createdAt - b.createdAt)
}

export async function saveOfflineActivity(activity: Omit<OfflineActivity, 'id' | 'createdAt' | 'updatedAt' | 'syncStatus' | 'attempts' | 'lastError'> & { clientRequestId?: string }) {
  const now = Date.now()
  const id = createClientRequestId('activity')
  const item: OfflineActivity = { ...activity, id, clientRequestId: activity.clientRequestId || id, createdAt: now, updatedAt: now, syncStatus: 'pendente', attempts: 0 }
  await runStore(ACTIVITIES_STORE, 'readwrite', (store) => store.put(item))
  return item
}

export async function updateOfflineActivity(activity: OfflineActivity) {
  const item = { ...activity, updatedAt: Date.now() }
  await runStore(ACTIVITIES_STORE, 'readwrite', (store) => store.put(item))
  return item
}

export async function removeOfflineActivity(id: string) {
  await deleteFromStore(ACTIVITIES_STORE, id)
  await removeOfflineAttachmentsByActivity(id)
}

export async function saveOfflineAttachment(attachment: Omit<OfflineAttachment, 'id' | 'createdAt'>) {
  const item: OfflineAttachment = { id: createClientRequestId('attachment'), createdAt: Date.now(), ...attachment }
  await runStore(ATTACHMENTS_STORE, 'readwrite', (store) => store.put(item))
  return item
}

export async function getOfflineAttachments(activityId: string): Promise<OfflineAttachment[]> {
  const db = await openOfflineDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ATTACHMENTS_STORE, 'readonly')
    const req = tx.objectStore(ATTACHMENTS_STORE).index('activityId').getAll(activityId)
    req.onsuccess = () => resolve((req.result || []).filter(isFresh))
    req.onerror = () => reject(req.error)
  })
}

export async function removeOfflineAttachmentsByActivity(activityId: string) {
  const attachments = await getOfflineAttachments(activityId)
  await Promise.all(attachments.map(attachment => deleteFromStore(ATTACHMENTS_STORE, attachment.id)))
}

export async function cleanupExpiredOfflineData() {
  const [activities, attachments] = await Promise.all([
    getAllFromStore<OfflineActivity>(ACTIVITIES_STORE),
    getAllFromStore<OfflineAttachment>(ATTACHMENTS_STORE),
  ])

  const expired = [
    ...(activities || []).filter(item => !isFresh(item)).map(item => [ACTIVITIES_STORE, item.id] as const),
    ...(attachments || []).filter(item => !isFresh(item)).map(item => [ATTACHMENTS_STORE, item.id] as const),
  ]

  await Promise.all(expired.map(([store, id]) => deleteFromStore(store, id)))
}

export async function getOfflineQueueSummary() {
  const queue = await getOfflineQueue()
  const byStatus = queue.reduce<Record<OfflineSyncStatus, number>>((acc, item) => {
    acc[item.syncStatus] += 1
    return acc
  }, { pendente: 0, sincronizando: 0, sincronizado: 0, erro: 0 })

  return {
    total: queue.length,
    pendente: byStatus.pendente,
    sincronizando: byStatus.sincronizando,
    erro: byStatus.erro,
    oldestCreatedAt: queue[0]?.createdAt || null,
  }
}


